import { Injectable } from '@angular/core';
import { createClient, mergeHeaders, type Client, type Options } from '@iana-rzms/bff-sdk';
import { AppConfigService } from './app.config.service';
import { CookieService } from 'ngx-cookie-service';

// Lightweight wrapper around the generated BFF SDK client so callers get
// consistent defaults (base URL, fetch credentials, XSRF/header merge) without
// reconfiguring the SDK on every call. Reuses a single client instance.
@Injectable({ providedIn: 'root' })
export class BffClientService {
  private client: Client | null = null;

  constructor(private appConfig: AppConfigService, private cookieService: CookieService) {}

  // Enriches a request options object with shared SDK defaults: reuse the
  // singleton client instance, attach XSRF + accept headers, merge caller headers,
  // and force fetch to send cookies (`credentials: include`). Callers pass their
  // specific request options and receive a fully-prepared options object.
  withDefaults<T extends Options>(options: T): T {
    const client = this.ensureClient();
    const xsrf = this.cookieService.get('RZMS-TOKEN');
    const defaultHeaders = {
      Accept: 'application/json,application/problem+json',
      'X-RZMS-XSRF-TOKEN': xsrf || ''
    };

    // mergeHeaders returns a Headers instance; convert to plain object before passing into SDK
    const mergedHeaders = mergeHeaders(defaultHeaders, options.headers as any);
    const plainHeaders: Record<string, string> = {};
    mergedHeaders.forEach((value, key) => {
      plainHeaders[key] = value as string;
    });

    return {
      ...options,
      client,
      credentials: 'include',
      headers: plainHeaders
    } as T;
  }

  private ensureClient(): Client {
    if (!this.client) {
      this.client = createClient({
        baseUrl: this.appConfig.config?.staffBackendUrl ?? '',
        credentials: 'include'
      });
    }
    return this.client;
  }
}
